import { useState } from "react";
import { FilterX, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImpersonationFilters } from "@/Api/impersonation";
import { TypeUser } from "@/types/userType";

export type TenantOption = {
  id: number;
  name: string;
};

interface ImpersonationFiltersProps {
  isSuperAdmin: boolean;
  tenants: TenantOption[];
  loading: boolean;
  onSearch: (filters: ImpersonationFilters) => void;
}

const ALL = "all";

export function ImpersonationFiltersBar({
  isSuperAdmin,
  tenants,
  loading,
  onSearch,
}: ImpersonationFiltersProps) {
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState(ALL);
  const [tenant, setTenant] = useState(ALL);
  const [status, setStatus] = useState(ALL);

  const mountFilters = (): ImpersonationFilters => {
    const filters: ImpersonationFilters = {};

    if (search.trim()) filters.search = search.trim();
    if (userType !== ALL) filters.user_type_id = userType;
    if (tenant !== ALL) filters.tenant_id = tenant;
    if (status !== ALL) filters.is_active = status;

    return filters;
  };

  const handleClear = () => {
    setSearch("");
    setUserType(ALL);
    setTenant(ALL);
    setStatus(ALL);

    onSearch({});
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
      <div className="space-y-2 lg:col-span-2">
        <Label htmlFor="search">Buscar</Label>
        <Input
          id="search"
          value={search}
          placeholder="Nome, e-mail, documento ou telefone"
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSearch(mountFilters());
          }}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="user-type">Tipo de usuário</Label>
        <Select value={userType} onValueChange={setUserType}>
          <SelectTrigger id="user-type">
            <SelectValue placeholder="Selecione" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos os tipos</SelectItem>
            <SelectItem value={String(TypeUser.client)}>Usuário da empresa</SelectItem>
            <SelectItem value={String(TypeUser.seller)}>Vendedor</SelectItem>
            <SelectItem value={String(TypeUser.unity_responsible)}>Responsável de unidade</SelectItem>
            <SelectItem value={String(TypeUser.stockist)}>Estoquista</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isSuperAdmin && (
        <div className="space-y-2">
          <Label htmlFor="tenant">Empresa</Label>
          <Select value={tenant} onValueChange={setTenant}>
            <SelectTrigger id="tenant">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas as empresas</SelectItem>
              {tenants.map((item) => (
                <SelectItem key={item.id} value={String(item.id)}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="status">Situação</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger id="status">
            <SelectValue placeholder="Selecione" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas</SelectItem>
            <SelectItem value="true">Ativos</SelectItem>
            <SelectItem value="false">Inativos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-end gap-2 lg:col-span-5">
        <Button onClick={() => onSearch(mountFilters())} disabled={loading}>
          <Search className="mr-2 h-4 w-4" />
          Buscar
        </Button>
        <Button variant="outline" onClick={handleClear} disabled={loading}>
          <FilterX className="mr-2 h-4 w-4" />
          Limpar
        </Button>
      </div>
    </div>
  );
}
